import { FC } from 'react'

import { useMandatoryProcessTasks } from 'generated/resources/datatable.graphql'
import {
  ProcessesProcessCategoryEnum,
  SelectorAccessesDocument,
  TasksValueObjectsTaskIdentifierEnum,
} from 'generated/resources/graphql'
import { isNil, omitBy } from 'lodash'

import DataTable from 'modules/core/components/DataTable'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { DataTableActions, EmptyState, projections } from './components'
import { filters as dataTableFilters } from './filters'
import { ProcessesTasksTableFiltersProvider } from './useTableFilters'

interface ProcessTasksDataTableProps {
  category: ProcessesProcessCategoryEnum
  processId: string
}

export const ProcessTasksDataTable = (props: ProcessTasksDataTableProps) => {
  return (
    <ProcessesTasksTableFiltersProvider>
      <ProcessDataTable {...props} />
    </ProcessesTasksTableFiltersProvider>
  )
}

const ProcessDataTable: FC<ProcessTasksDataTableProps> = ({ category, processId }) => {
  const { navigation } = useNavigation()

  const { data } = useQuery(SelectorAccessesDocument)

  const accessesOptions = data?.apiCore.accesses

  const workflowOfByType = {
    onboarding: i18n.t('processes.process.overview.tasks_table.onboarding'),
    offboarding: i18n.t('processes.process.overview.tasks_table.offboarding'),
    training: i18n.t('processes.process.overview.tasks_table.training'),
    custom: i18n.t('processes.process.overview.tasks_table.custom'),
  }

  const contractStartDateByType = {
    onboarding: i18n.t('processes.process.overview.tasks_table.start_date'),
    offboarding: i18n.t('processes.process.overview.tasks_table.end_date'),
    training: null,
    custom: null,
  }

  const workflowTitle = workflowOfByType[category]
  const contractStartDateTitle = contractStartDateByType[category]

  const projectionProps = {
    affecteeTitle: workflowTitle,
    contractStartDateTitle,
    category,
    pathname: (taskId: string, identifier: TasksValueObjectsTaskIdentifierEnum) =>
      navigation.tasks.taskIdAndIdentifier.fullPath({ taskId: taskId, identifier }),
  }

  const variables = omitBy(
    {
      mandatory: true,
    },
    isNil
  )

  const source = useMandatoryProcessTasks(projections(projectionProps), {
    variables: {
      ...variables,
      processId,
      category,
    },
  })

  if (!accessesOptions) return null

  return (
    <DataTable.Provider
      filters={dataTableFilters(category, source, accessesOptions)}
      source={source}
      pagination={{ kind: 'offset', itemsPerPage: 10 }}
      rowTo={({ materializedProcessId, materializedItemId }) =>
        navigation.processes.template.id.processInstance.item.fullPath({
          category,
          id: processId,
          materializedProcessId: String(materializedProcessId),
          materializedItemId: String(materializedItemId),
        })
      }
    >
      <ProcessTasksDataContent />
    </DataTable.Provider>
  )
}
const date = new Date()
const formattedDate = date.toISOString().slice(0, 19).replace('T', '-').replace(/:/g, '-')
const filename = `processes-tasks-${formattedDate}`

const ProcessTasksDataContent = () => {
  return (
    <>
      <DataTable.HeadBar exportFileName={filename} actions={<DataTableActions />} />
      <DataTable.Table autoWidth selectable={() => true} emptyState={<EmptyState />} />
    </>
  )
}
